import { can } from '@iq/react-components';
import { createSelector } from 'reselect';
import config from '../../config';

export const getGrants = (state) => state.auth.grants;

export const getMembers = createSelector(
  (state) => state.auth.members,
  (_, objectId) => objectId,
  (members, objectId) => {
    const membersAsArray = Object.values(members);
    if (!objectId) {
      return membersAsArray;
    }
    return membersAsArray.filter((role) => role.objectId === objectId);
  }
);

export const getMemberTypes = (state) => state.auth.memberTypes;

export const isLoadingMembers = (state) => state.auth.loadingMembers;

export const isLoadingTenantUsers = (state) => state.auth.loadingTenantUsers;

export const getTenantUsers = createSelector(
  (state) => state.auth.tenantUsers,
  (tenantUsers) =>
    Object.entries(tenantUsers)
      .reduce((acc, [id, userData]) => [...acc, { id, ...userData }], [])
      .sort((u1, u2) => u1.name.localeCompare(u2.name))
);

export const getInvitations = (state) => state.auth.invitations;

export const isLoadingInvitations = (state) => state.auth.loadingInvitations;

export const hasCheckedInvitations = (state) => state.auth.checkedInvitations;

export const getHasPermission = createSelector(
  getGrants,
  (_, permission, scope) => [permission, scope],
  (grants, [permission, scope]) => {
    if (!(grants && permission)) return false;
    const check = [grants, permission];
    if (scope) check.push(scope);
    return can(...check);
  }
);

export const getIsTenantOrSuperAdmin = createSelector(
  getGrants,
  (_, org) => org,
  (grants, org) => {
    if (!(grants && org)) return false;
    return [can(grants, 'sites/Create', { org }), can(grants, 'sites/Create', {})].some(
      (grant) => grant
    );
  }
);

export const getIsAdmin = createSelector(
  getGrants,
  (_, site) => site,
  (grants, site) => {
    if (!(grants && site?.id && site?.org)) return false;
    return can(grants, 'users/Read', { org: site.org });
  }
);

export const getIsContributorOrAdmin = createSelector(
  getGrants,
  (_, site) => site,
  (grants, site) => {
    if (!(grants && site?.id && site?.org)) return false;
    return [
      can(grants, 'members/Write', { org: site.org, site: site.id }),
      can(grants, 'sites/Write', { org: site.org, site: site.id }),
      can(grants, 'sources/Write', { org: site.org, site: site.id }),
      can(grants, 'visualizations/Write', { org: site.org, site: site.id }),
      can(grants, 'models/Write', { org: site.org, site: site.id }),
    ].some((grant) => grant);
  }
);

const getServices = () => (config?.services ? Object.keys(config.services) : []);

export const hasDocsReadPermission = createSelector(
  [getServices, (state) => state],
  (apiServiceNames, state) =>
    apiServiceNames.some((serviceName) => getHasPermission(state, `${serviceName}Docs/Read`))
);

export const getDocsReadPermissions = createSelector(
  [getServices, (state) => state],
  (apiServiceNames, state) =>
    apiServiceNames
      .map((serviceName) => `${serviceName}Docs/Read`)
      .filter((permission) => getHasPermission(state, permission))
);

export const getServiceAccounts = (state) => state.auth.serviceAccounts;

export const isLoadingServiceAccounts = (state) => state.auth.loadingServiceAccounts;

export const isTransferringServiceAccounts = (state) => state.auth.transferringServiceAccounts;
